import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { isInternalUser } from '../services/accessControlService';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    // Check if user has a saved preference
    const savedTheme = localStorage.getItem('darkMode');
    return savedTheme ? JSON.parse(savedTheme) : false;
  });

  useEffect(() => {
    // Update localStorage when darkMode changes
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    
    // Update document class for Tailwind dark mode
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save to user's Firestore document if user is authenticated
    const saveToFirestore = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          // Determine the correct collection based on user type
          const isInternal = await isInternalUser(user.uid);
          const collectionName = isInternal ? 'internal_users' : 'users';
          const userDoc = doc(db, collectionName, user.uid);
          
          await updateDoc(userDoc, {
            darkMode: darkMode,
            lastUpdated: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error saving dark mode preference to Firestore:', error);
        }
      }
    };

    saveToFirestore();
  }, [darkMode]);

  // Load dark mode preference from Firestore when user logs in
  useEffect(() => {
    const loadUserPreference = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          // Determine the correct collection based on user type
          const isInternal = await isInternalUser(user.uid);
          const collectionName = isInternal ? 'internal_users' : 'users';
          const userDoc = doc(db, collectionName, user.uid);
          const docSnap = await getDoc(userDoc);
          
          if (docSnap.exists()) {
            const userData = docSnap.data();
            if (userData.darkMode !== undefined) {
              setDarkMode(userData.darkMode);
              localStorage.setItem('darkMode', JSON.stringify(userData.darkMode));
            }
          }
        } catch (error) {
          console.error('Error loading dark mode preference from Firestore:', error);
        }
      }
    };

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        loadUserPreference();
      }
    });

    return () => unsubscribe();
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(prevMode => !prevMode);
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
} 